import { useEffect, useMemo, useState } from 'react'
import { officeReviewStatusesApi, type OfficeReviewStatus } from '@/services/officeReviewStatuses'

/**
 * 事務局確認用ステータス（顧客要望 管理No.25）のマスタを取得する共有フック。
 *
 * マスタは自治体ごとに固定的で頻繁には変わらないため、モジュールレベルでキャッシュして
 * 画面横断（編集／詳細／検索／ダッシュボード）での重複取得を避ける。
 * 画面再読込でキャッシュはリセットされる。
 */
let cache: OfficeReviewStatus[] | null = null
let inflight: Promise<OfficeReviewStatus[]> | null = null

/**
 * モジュールキャッシュを破棄する。自治体マスタ管理（追加/編集/無効化, 管理No.25）後に呼び、
 * 次回 useOfficeReviewStatuses で最新候補を取り直させる。
 * 注: キャッシュはログインユーザーの自治体スコープ基準のため、他自治体を編集した場合は
 * 自分の表示に影響しない（タブ単位の最適化として許容）。
 */
export function invalidateOfficeReviewStatusesCache(): void {
  cache = null
  inflight = null
}

async function loadOnce(): Promise<OfficeReviewStatus[]> {
  if (cache) return cache
  if (!inflight) {
    inflight = officeReviewStatusesApi
      .list()
      .then(({ data }) => {
        cache = data ?? []
        return cache
      })
      .catch((error) => {
        inflight = null
        throw error
      })
  }
  return inflight
}

export interface UseOfficeReviewStatusesResult {
  statuses: OfficeReviewStatus[]
  byId: Map<number, OfficeReviewStatus>
  loading: boolean
}

export function useOfficeReviewStatuses(): UseOfficeReviewStatusesResult {
  const [statuses, setStatuses] = useState<OfficeReviewStatus[]>(cache ?? [])
  const [loading, setLoading] = useState(cache === null)

  useEffect(() => {
    if (cache) return
    let active = true
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    loadOnce()
      .then((list) => {
        if (active) {
          setStatuses(list)
          setLoading(false)
        }
      })
      .catch(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  const byId = useMemo(() => {
    const map = new Map<number, OfficeReviewStatus>()
    for (const status of statuses) map.set(status.id, status)
    return map
  }, [statuses])

  return { statuses, byId, loading }
}
